import React, { useCallback, useMemo, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import Seo from "../components/Seo";
import Reveal from "../components/omega/Reveal";
import PhotoImage from "../components/fotografia/PhotoImage";
import FotoLightbox from "../components/fotografia/FotoLightbox";
import WhatsAppCta from "../components/fotografia/WhatsAppCta";
import { trabajoPorPath } from "../data/trabajos";
import { SITE_NAME } from "../seo/config";
import { SIZES } from "../utils/photos";

/**
 * Trabajo interno con estilo de portafolio.
 *
 * Misma gramática que las entregas: las fotos sobre el campo oscuro (navy) y
 * el texto que invita a escribir sobre papel (sand). No hay precios, packs ni
 * barra fija de precio (MobileActionBar se oculta en estas rutas): la página
 * existe para mostrar el trabajo, y el único llamado es "quiero algo así".
 *
 * noindex, igual que una entrega: es una página interna que se comparte por
 * enlace. public/_headers repite la orden en la cabecera HTTP, que es la que
 * respetan también las imágenes.
 *
 * El orden del visor es el mismo que el de la página — portada, rejilla y
 * fotos anchas al final — para que "siguiente" signifique lo que se ve.
 */

const SIZES_ANCHA = "(min-width: 1152px) 1152px, 100vw";

const TrabajoPage = () => {
  const { pathname } = useLocation();
  const trabajo = trabajoPorPath(pathname);
  const [abierta, setAbierta] = useState(null);
  const cerrar = useCallback(() => setAbierta(null), []);

  const { rejilla, anchas, orden } = useMemo(() => {
    if (!trabajo) return { rejilla: [], anchas: [], orden: [] };
    const grid = trabajo.fotos.filter((foto) => !foto.ancha);
    const wide = trabajo.fotos.filter((foto) => foto.ancha);
    return { rejilla: grid, anchas: wide, orden: [trabajo.portada, ...grid, ...wide] };
  }, [trabajo]);

  if (!trabajo) return <Navigate to="/" replace />;

  const { portada } = trabajo;
  const abrir = (foto) => setAbierta(orden.findIndex((item) => item.id === foto.id));

  return (
    <>
      <Seo
        title={`${trabajo.titulo}, ${trabajo.lugar} | ${SITE_NAME}`}
        description={trabajo.resumen}
        path={trabajo.path}
        noindex
      />

      <section className="bg-navy-900 pb-16 pt-28 text-white md:pb-20 md:pt-32">
        <div className="container-page grid items-center gap-12 lg:grid-cols-[minmax(0,1fr)_minmax(0,0.85fr)]">
          <div className="hero-enter">
            <p className="text-sand-300">Portafolio</p>
            <h1 className="mt-4 font-display text-display-md font-bold text-white">{trabajo.titulo}</h1>
            <p className="mt-3 font-mono text-sm uppercase tracking-label text-navy-200">
              {trabajo.lugar} · {trabajo.fecha}
            </p>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-navy-100">{trabajo.resumen}</p>

            <dl className="mt-10 grid max-w-xl grid-cols-2 gap-x-8 gap-y-6 border-t border-navy-700 pt-8">
              {trabajo.datos.map((dato) => (
                <div key={dato.etiqueta}>
                  <dt className="text-sm text-navy-300">{dato.etiqueta}</dt>
                  <dd className="mt-1 font-display text-lg font-bold text-white">{dato.valor}</dd>
                </div>
              ))}
            </dl>

            <div className="mt-10 flex flex-col gap-3 sm:flex-row">
              <a href="#galeria" className="btn-on-dark w-full sm:w-auto">
                Ver la galería
              </a>
              <WhatsAppCta
                message={trabajo.whatsapp}
                location="trabajo_hero"
                variant="outline"
                className="w-full sm:w-auto"
              >
                Quiero algo así
              </WhatsAppCta>
            </div>
          </div>

          <button
            type="button"
            onClick={() => abrir(portada)}
            className="group block overflow-hidden rounded-2xl focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-sand-300"
            aria-label={`Ampliar: ${portada.alt}`}
          >
            <PhotoImage
              foto={portada}
              sizes={SIZES.half}
              priority
              className="aspect-[4/5] w-full object-cover transition duration-700 group-hover:scale-[1.02]"
            />
          </button>
        </div>
      </section>

      <section id="galeria" className="scroll-mt-20 bg-navy-900 pb-20 md:pb-28">
        <div className="container-page">
          <div className="grid grid-cols-2 gap-3 md:gap-4 lg:grid-cols-3">
            {rejilla.map((foto) => (
              <Reveal as="figure" key={foto.id} y={16} amount={0.15}>
                <button
                  type="button"
                  onClick={() => abrir(foto)}
                  className="group block w-full overflow-hidden rounded-xl focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sand-300"
                  aria-label={`Ampliar: ${foto.alt}`}
                >
                  <PhotoImage
                    foto={foto}
                    sizes={SIZES.grid3}
                    className="h-auto w-full transition duration-700 group-hover:scale-[1.03]"
                  />
                </button>
              </Reveal>
            ))}
            {anchas.map((foto) => (
              <Reveal as="figure" key={foto.id} y={16} amount={0.15} className="col-span-full">
                <button
                  type="button"
                  onClick={() => abrir(foto)}
                  className="group block w-full overflow-hidden rounded-xl focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sand-300"
                  aria-label={`Ampliar: ${foto.alt}`}
                >
                  <PhotoImage
                    foto={foto}
                    sizes={SIZES_ANCHA}
                    className="h-auto w-full transition duration-700 group-hover:scale-[1.01]"
                  />
                </button>
                <figcaption className="mt-3 text-sm text-navy-300">{foto.alt}.</figcaption>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-sand-50 py-20 md:py-28">
        <div className="container-page max-w-3xl">
          <h2 className="section-title">¿Quieres algo así para tu escuela o club?</h2>
          <p className="section-copy mt-5 max-w-xl">
            Fotografiamos entrenamientos, partidos y torneos en tu cancha, y entregamos las fotos editadas
            y en vertical, listas para las redes de la escuela y para los apoderados.
          </p>
          <WhatsAppCta message={trabajo.whatsapp} location="trabajo_cta" variant="dark" className="mt-8">
            Escribir por WhatsApp
          </WhatsAppCta>
        </div>
      </section>

      <FotoLightbox fotos={orden} indice={abierta} onCambiar={setAbierta} onCerrar={cerrar} />
    </>
  );
};

export default TrabajoPage;
